import React from 'react';
import PropTypes from 'prop-types';

/**
 * IconSelector Component
 * Displays a horizontally scrollable list of icons for selection
 * @param {Array} icons - List of icon objects with _id and icon_url
 * @param {String} selectedIconId - Currently selected icon ID
 * @param {Function} onSelect - Callback when an icon is selected
 * @param {Boolean} disabled - Whether the selector is disabled
 * @param {Boolean} loading - Whether icons are loading
 */
const IconSelector = ({
  icons = [],
  selectedIconId,
  onSelect,
  disabled = false,
  loading = false
}) => {
  const handleIconClick = (iconId) => {
    if (!disabled && onSelect) {
      onSelect(iconId);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shoorah-primary"></div>
        <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">Loading icons...</span>
      </div>
    );
  }

  if (!icons || icons.length === 0) {
    return (
      <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
        No icons available
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Horizontal scrollable container */}
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500">
        {icons.map((icon) => {
          const isSelected = selectedIconId === icon._id;
          return (
            <button
              key={icon._id}
              type="button"
              onClick={() => handleIconClick(icon._id)}
              disabled={disabled}
              className={`
                relative flex-shrink-0 w-16 h-16 rounded-lg border-2 transition-all duration-200
                flex items-center justify-center p-2
                ${
                  isSelected
                    ? 'border-shoorah-primary bg-shoorah-primary/10 '
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-shoorah-darkBgColor hover:border-shoorah-secondary'
                }
                ${
                  disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer hover:shadow-md active:scale-95'
                }
              `}
              title={icon.name || 'Select icon'}
            >
              {icon.icon_url ? (
                <>
                  <img
                    src={icon.icon_url}
                    alt={icon.name || 'Icon'}
                    className={`w-full h-full object-contain  ${isSelected ? 'scale-110' : ''}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) {
                        fallback.style.display = 'flex';
                      }
                    }}
                  />
                  {/* Fallback if image fails to load */}
                  <div className="hidden w-full h-full items-center justify-center text-gray-400 text-xs">
                    No icon
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                  No icon
                </div>
              )}

              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-shoorah-primary rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Scroll hint */}
      {icons.length > 5 && (
        <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white dark:from-shoorah-darkBgTabColor to-transparent pointer-events-none" />
      )}
    </div>
  );
};

IconSelector.propTypes = {
  icons: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      icon_url: PropTypes.string,
      name: PropTypes.string
    })
  ),
  selectedIconId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool
};

export default IconSelector;
